import { CashEntryModel } from '../model/single-transaction/cash-entry/cash-entry.model'
import { AggInfoJson } from '../model/single-transaction/security-transfer/aggInfo.model'
import { TradeEntryModel } from '../model/single-transaction/trade/trade-entry.model'

/**
 * Create DynamicRows for ExternalTransaction
 */
export function createDynamicRowsforExternalTransaction(
  aggInfoJson: AggInfoJson | undefined,
  source: 'custody' | 'trading',
): CashEntryModel[] | TradeEntryModel[] {
  const dynamicRows: (CashEntryModel | TradeEntryModel)[] = []

  // Check if heliosDisplay exists and has data
  if (
    !aggInfoJson?.heliosDisplay ||
    typeof aggInfoJson.heliosDisplay !== 'string'
  ) {
    return dynamicRows as CashEntryModel[] | TradeEntryModel[]
  }

  // Parse the heliosDisplay field - it contains comma-separated field names
  const displayFields = aggInfoJson.heliosDisplay
    .split(',')
    .map((field: string) => field.trim())
    .filter((field: string) => field.length > 0)

  if (displayFields.length === 0) {
    return dynamicRows as CashEntryModel[] | TradeEntryModel[]
  }

  // Create dynamic rows for each field specified in heliosDisplay
  displayFields.forEach((fieldName: string) => {
    // Get the value from aggInfoJson using the field name generically
    const fieldValue = (aggInfoJson as Record<string, unknown>)[fieldName]

    if (fieldValue !== undefined && fieldValue !== null) {
      // Convert field name to display format (e.g., "FX_Rate" -> "FX Rate")
      const displayName = fieldName.replace(/_/g, ' ')
      const displayValue = formatValue(
        (aggInfoJson as Record<string, unknown>)[fieldName],
      )

      // Create a dynamic row entry - common properties for both types
      const commonRowData = {
        id: fieldName,
        validityStatus: 'valid' as const,
        isEditable: false,
        type: typeof displayValue === 'number' ? 'number' : 'string',
        field: displayName,
        value: '',
        custody: source === 'custody' ? displayValue + '' : '',
        trading: source === 'trading' ? displayValue + '' : '',
        errorMessage: '',
      }

      // Type assertion based on transaction type
      const dynamicRow = commonRowData as CashEntryModel | TradeEntryModel
      dynamicRows.push(dynamicRow)
    }
  })

  return dynamicRows as CashEntryModel[] | TradeEntryModel[]
}

const DYNAMIC_ROW_TOLERANCE = 0.0001

/**
 * Create DynamicRows For CWTransaction
 */
export function createDynamicRowsForCWTransaction(
  custodyAggInfo: Record<string, unknown>,
  tradingAggInfo: Record<string, unknown>,
): (CashEntryModel | TradeEntryModel)[] {
  const dynamicRows: (CashEntryModel | TradeEntryModel)[] = []
  const fieldMap = new Map<string, { custody?: unknown; trading?: unknown }>()

  if (
    custodyAggInfo?.heliosDisplay &&
    typeof custodyAggInfo.heliosDisplay === 'string'
  ) {
    const custodyFields = custodyAggInfo.heliosDisplay
      .split(',')
      .map((field: string) => field.trim())
      .filter((field: string) => field.length > 0)

    custodyFields.forEach((fieldName: string) => {
      const fieldValue = custodyAggInfo[fieldName]
      if (fieldValue !== undefined && fieldValue !== null) {
        if (!fieldMap.has(fieldName)) {
          fieldMap.set(fieldName, {})
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        fieldMap.get(fieldName)!.custody = fieldValue
      }
    })
  }

  // Process trading aggInfo
  if (
    tradingAggInfo?.heliosDisplay &&
    typeof tradingAggInfo.heliosDisplay === 'string'
  ) {
    const tradingFields = tradingAggInfo.heliosDisplay
      .split(',')
      .map((field: string) => field.trim())
      .filter((field: string) => field.length > 0)

    tradingFields.forEach((fieldName: string) => {
      const fieldValue = tradingAggInfo[fieldName]
      if (fieldValue !== undefined && fieldValue !== null) {
        if (!fieldMap.has(fieldName)) {
          fieldMap.set(fieldName, {})
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        fieldMap.get(fieldName)!.trading = fieldValue
      }
    })
  }

  // Create dynamic rows from the merged field map
  fieldMap.forEach((values, fieldName) => {
    const displayName = fieldName.replace(/_/g, ' ')
    const custodyStr = values.custody ? formatValue(values.custody) : ''
    const tradingStr = values.trading ? formatValue(values.trading) : ''
    const { validityStatus, errorMessage } = getDynamicRowDiffStatus(
      values.custody,
      values.trading,
    )
    const commonRowData = {
      id: fieldName,
      validityStatus,
      isEditable: false,
      type:
        typeof (custodyStr || tradingStr) === 'number' ? 'number' : 'string',
      field: displayName,
      value: '',
      custody: custodyStr,
      trading: tradingStr,
      errorMessage,
    }
    const dynamicRow = commonRowData as CashEntryModel | TradeEntryModel
    dynamicRows.push(dynamicRow)
  })

  return dynamicRows
}

/**
 *
 */
function getDynamicRowDiffStatus(
  custodyValue: unknown,
  tradingValue: unknown,
): { validityStatus: string; errorMessage: string } {
  if (!custodyValue || !tradingValue) {
    return { validityStatus: 'valid', errorMessage: '' }
  }

  const custodyNum = Number(custodyValue)
  const tradingNum = Number(tradingValue)

  if (isNaN(custodyNum) || isNaN(tradingNum)) {
    return { validityStatus: 'valid', errorMessage: '' }
  }

  const diff = tradingNum - custodyNum
  if (Math.abs(diff) > DYNAMIC_ROW_TOLERANCE) {
    return {
      validityStatus: 'warning',
      errorMessage: `Difference of ${parseFloat(
        diff.toFixed(4),
      )} between Trading and Custody`,
    }
  }

  return { validityStatus: 'valid', errorMessage: '' }
}

/** Converts a numeric value or numeric string to a number; returns non-numeric values as strings */
export function formatValue(value: unknown): string | number {
  const num = Number(value)
  if (!isFinite(num) || (typeof value === 'string' && value.trim() === ''))
    return String(value)
  return num
}
